import { useState } from 'react';
import MetricCard from '../MetricCard';
import ChartCard from '../charts/ChartCard';
import CustomTooltip from '../charts/CustomTooltip';
import TimePeriodToggle from './TimePeriodToggle';
import { formatNumber } from '../../utils/formatters';
import { groupByTimePeriod } from '../../utils/timePeriod';
import { CHART_COLORS, CHART_THEME } from '../../utils/chartColors';
import {
  FiTrendingDown,
  FiAlertTriangle,
  FiCheckCircle,
  FiHash
} from 'react-icons/fi';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';

const ChurnTab = ({ data }) => {
  const [timePeriod, setTimePeriod] = useState('monthly');

  if (!data) return null;

  const { churn, timeSeries } = data;

  const monthlyChurnTrend = churn?.monthlyChurnTrend || [];
  const tenureDistribution = churn?.tenureDistribution || [];
  const recentCancellations = churn?.recentCancellations || [];

  // Net growth waterfall from timeSeries
  const grouped = groupByTimePeriod(timeSeries || [], timePeriod);
  const growthWaterfall = grouped.map(d => ({
    period: d.period,
    new: d.newSubs,
    churned: -d.canceledSubs,
    net: d.newSubs - d.canceledSubs,
  }));

  const isDailyView = timePeriod === 'daily';

  // Tenure bar colors (gradient effect)
  const tenureColors = ['#a5b4fc', '#818cf8', '#6366f1', '#4f46e5', '#4338ca'];

  return (
    <div className="space-y-6">
      {/* Metric Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          label="Monthly Churn Rate"
          value={`${churn.monthlyChurnRate}%`}
          icon={FiTrendingDown}
        />
        <MetricCard
          label="Churned (30d)"
          value={formatNumber(churn.churnCountLast30)}
          icon={FiHash}
        />
        <MetricCard
          label="At Risk"
          value={formatNumber(churn.atRiskCount)}
          subtitle="Cancel at period end"
          icon={FiAlertTriangle}
        />
        <MetricCard
          label="Trial Conversion"
          value={`${churn.trialConversionRate}%`}
          icon={FiCheckCircle}
        />
      </div>

      {/* Time Period Toggle */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-700">Growth Trends</h3>
        <TimePeriodToggle value={timePeriod} onChange={setTimePeriod} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Churn Rate Trend (always monthly) */}
        <ChartCard title="Churn Rate Trend" subtitle="Monthly churn rate over last 12 months">
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={monthlyChurnTrend}>
              <defs>
                <linearGradient id="churnGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={CHART_COLORS.danger} stopOpacity={0.1} />
                  <stop offset="95%" stopColor={CHART_COLORS.danger} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...CHART_THEME.grid} />
              <XAxis dataKey="month" tick={CHART_THEME.axis} />
              <YAxis tick={CHART_THEME.axis} tickFormatter={(v) => `${v}%`} />
              <Tooltip content={<CustomTooltip formatter={(val, name) => name === 'Rate' ? `${val}%` : val} />} />
              <Area
                type="monotone"
                dataKey="rate"
                stroke={CHART_COLORS.danger}
                strokeWidth={2}
                fill="url(#churnGradient)"
                name="Rate"
                dot={{ r: 3, fill: CHART_COLORS.danger }}
                activeDot={{ r: 5 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Tenure Distribution */}
        <ChartCard title="Subscription Tenure" subtitle="How long active subscribers have been around">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={tenureDistribution} barSize={40}>
              <CartesianGrid {...CHART_THEME.grid} />
              <XAxis dataKey="bucket" tick={{ ...CHART_THEME.axis, fontSize: 10 }} />
              <YAxis tick={CHART_THEME.axis} />
              <Tooltip content={<CustomTooltip formatter={(val) => `${val} subscriptions`} />} />
              <Bar dataKey="count" name="Subscriptions" radius={[4, 4, 0, 0]}>
                {tenureDistribution.map((entry, i) => (
                  <Cell key={i} fill={tenureColors[i % tenureColors.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Net Growth Waterfall */}
        <ChartCard title="Net Growth Waterfall" subtitle={`New, churned, and net subscriber change — ${timePeriod}`} className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={growthWaterfall} barGap={2}>
              <CartesianGrid {...CHART_THEME.grid} />
              <XAxis dataKey="period" tick={CHART_THEME.axis} angle={isDailyView ? -45 : 0} textAnchor={isDailyView ? 'end' : 'middle'} height={isDailyView ? 50 : 30} />
              <YAxis tick={CHART_THEME.axis} />
              <Tooltip content={<CustomTooltip />} />
              <Legend iconType="circle" iconSize={8} />
              <ReferenceLine y={0} stroke="#cbd5e1" />
              <Bar dataKey="new" fill={CHART_COLORS.success} name="New" radius={[3, 3, 0, 0]} barSize={isDailyView ? 8 : 18} />
              <Bar dataKey="churned" fill={CHART_COLORS.danger} name="Churned" radius={[0, 0, 3, 3]} barSize={isDailyView ? 8 : 18} />
              <Bar dataKey="net" fill={CHART_COLORS.info} name="Net" radius={[3, 3, 0, 0]} barSize={isDailyView ? 8 : 18} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Recent Cancellations */}
      {recentCancellations.length > 0 && (
        <div className="card">
          <h3 className="text-sm font-medium text-gray-700 mb-4">Recent Cancellations</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Customer</th>
                  <th className="text-right py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Amount</th>
                  <th className="text-right py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Interval</th>
                  <th className="text-right py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Tenure</th>
                  <th className="text-right py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Canceled</th>
                  <th className="text-center py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Refund</th>
                </tr>
              </thead>
              <tbody>
                {recentCancellations.map((c, i) => (
                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="py-3">
                      <p className="text-sm text-gray-900">{c.customerEmail}</p>
                      <p className="text-xs text-gray-400 font-mono">{c.subscriptionId.slice(0, 20)}...</p>
                    </td>
                    <td className="text-right py-3 text-sm text-gray-900 tabular-nums">
                      ${formatNumber(Math.round(c.amount / 100))}
                    </td>
                    <td className="text-right py-3 text-sm text-gray-600">{c.interval}</td>
                    <td className="text-right py-3 text-sm text-gray-600 tabular-nums">
                      {c.tenure > 0 ? `${c.tenure}d` : '<1d'}
                    </td>
                    <td className="text-right py-3 text-sm text-gray-600">
                      {c.canceledAt ? new Date(c.canceledAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}
                    </td>
                    <td className="text-center py-3">
                      {c.hadRefund ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700">Yes</span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-500">No</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChurnTab;
